import tw, { css } from "twin.macro"
import React, { useMemo } from "react"
import { StyledBodyText } from "../bodyText"
import { documentToReactComponents, Options } from "@contentful/rich-text-react-renderer"
import { BLOCKS, INLINES } from "@contentful/rich-text-types"
import EmbedAsset from "../embed/asset"
import EmbedLarge from "../embed/large"
import EmbedMedium from "../embed/medium"
import { FeatureProps } from "../../pages/features/[slug]"
import EmbedFullWidth from "../embed/fullWidth"
import EmbedSmallLeft from "../embed/smallLeft"
import EmbedSmallRight from "../embed/smallRight"
import EmbedMediumRight from "../embed/medium-right"

enum Embed {
  FullWidth = "full-width",
  Large = "large",
  Medium = "medium",
  MediumRight = "medium-right",
  SmallLeft = "small-left",
  SmallRight = "small-right",
}

function renderOptions(links: any): Options {
  // create an asset map
  const assetMap = new Map()
  // loop through the assets and add them to the map
  for (const asset of links.assets.block) {
    assetMap.set(asset.sys.id, asset)
  }
  for (const asset of links.assets.hyperlink) {
    assetMap.set(asset.sys.id, asset)
  }

  // create an entry map
  const entryBlockMap = new Map()

  // loop through the block linked entries and add them to the map
  for (const entry of links.entries.block) {
    entryBlockMap.set(entry.sys.id, entry)
  }

  const entryMap = new Map()
  // loop through the inline linked entries and add them to the map
  for (const entry of links.entries.inline) {
    entryMap.set(entry.sys.id, entry)
  }

  return {
    // other options...
    renderNode: {
      [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        // find the asset in the assetMap by ID
        const asset = assetMap.get(node.data.target.sys.id)
        // render the asset accordingly
        return (
          <div tw="">
            <EmbedAsset
              src={asset.url}
              height={asset.height}
              width={asset.width}
              description={asset?.description || ""}
            />
          </div>
        )
      },

      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        // find the entry in the entryMap by ID
        const entry = entryBlockMap.get(node.data.target.sys.id)
        // referenced in the GraphQL query

        if (entry.__typename === "Card") {
          switch (entry.layout) {
            case Embed.FullWidth:
              return <EmbedFullWidth {...entry} />
            case Embed.Large:
              return <EmbedLarge {...entry} />
            case Embed.Medium:
              return <EmbedMedium {...entry} />
            case Embed.MediumRight:
              return <EmbedMediumRight {...entry} />
            case Embed.SmallLeft:
              return <EmbedSmallLeft {...entry} />
            case Embed.SmallRight:
              return <EmbedSmallRight {...entry} />
            default:
              return <EmbedLarge {...entry} />
          }
        }
      },
      [INLINES.ASSET_HYPERLINK]: (node, children) => {
        // find the asset in the assetMap by ID
        const asset = assetMap.get(node.data.target.sys.id)
        // render the asset accordingly
        const text = `${children}`
        const textCls = text.replace(/\[|\]/g, "")
        return (
          <EmbedAsset
            src={asset.url}
            height={asset.height}
            width={asset.width}
            description={asset?.description || ""}
          />
        )
      },
    },
  }
}

const Feature = (props: FeatureProps) => {
  const { preview } = props
  const { title, deck, body, kicker } = props.page
  const bodyJson = body.json
  const bodyLinks = body.links

  const bodyText = useMemo(() => {
    return documentToReactComponents(bodyJson, renderOptions(bodyLinks))
  }, [body])

  const draft = preview && (
    <div tw="flex justify-center">
      <p tw="bg-yellow-500 p-1 px-4 text-xs font-sans rounded-full">DRAFT</p>
    </div>
  )

  return (
    <section tw="py-10 space-y-10">
      <div tw="max-w-screen-desktop-lg desktop-lg:mx-auto px-4 relative">
        <div tw="grid grid-cols-4 tablet-lg:(grid-cols-12) gap-4 gap-y-4 desktop:(gap-6 gap-y-16)">
          <div tw="col-span-4 tablet-lg:(col-span-8 col-start-3)">
            <div tw="pt-10 space-y-8">
              {draft}
              <div>
                {kicker && <p tw="font-sans text-center text-xl uppercase">{kicker}</p>}
                <h1
                  tw="font-sans text-3xl tablet:text-h1 font-normal text-center"
                  css={[
                    css`
                      letter-spacing: 0.04em;
                    `,
                  ]}
                >
                  <span>{title}</span>
                </h1>
              </div>
              <h2 tw="text-xl tablet:(text-2xl) text-center font-serif">{deck}</h2>
            </div>
          </div>
          <div tw="col-span-4 tablet-lg:(col-span-12)">
            <StyledBodyText>
              <div className="feature">{bodyText}</div>
            </StyledBodyText>
            <div tw="pt-24 text-center">•••</div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Feature
